import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import moment from "moment";
import { get, isEmpty, pick } from "radash";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import ResmedSignIn from "src/containers/SignInPage/ResmedSignIn";
import AuthService from "src/services/authService";
import UserService from "src/services/userService";
import { parseQueryParams, useQuery } from "src/services/utils";
import { injectAuthUser, updateAuthUser } from "src/store/actions/auth";

import FormHeaderSection from "@components/FormHeaderSection";
import LoadingButton from "@components/LoadingButton";
import StepTracker from "@components/StepTracker/StepTracker";

import ReferralWelcomeCard from "@containers/Cards/ReferralWelcomeCard";
import SleepCard from "@containers/Cards/SleepCard";
import RegistrationForm from "@containers/Forms/RegistrationForm";
import CodeVerification, {
  CODE_VERIFICATION_FORM_ID,
} from "@containers/Forms/RegistrationForm/CodeVerification";
import EmailVerificationForm, {
  EMAIL_VERIF_FORM_ID,
} from "@containers/Forms/RegistrationForm/EmailVerificationForm";
import PatientDetailsForm, {
  PATIENT_DETAILS_FORM_ID,
} from "@containers/Forms/RegistrationForm/PatientDetailsForm";
import { REGISTRATION_FORM_ID } from "@containers/Forms/RegistrationForm/RegistrationForm";
import { EventInfo } from "@containers/ProviderProfile/ProviderProfile";

import { getRecordId, handleDynamicFormSubmission } from "@utils/helpers";
import { LStorage } from "@utils/localStorage";

import ScheduleAppointment from "./ScheduleAppointment";
import TermsConsent from "./TermsConsent";

const resmedLogoPath = "/assets/external-logos/resmed-logo-large.svg";
const STEPS = {
  register: 1,
  schedule: 2,
  emailSignup: 3,
  verification: 4,
  details: 5,
  confirmation: 6,
};

const totalSteps = Object.values(STEPS).length;
const Registration = ({
  setInitialData,
  handleNoAvailability,
}: {
  setInitialData: (data: object) => void;
  handleNoAvailability: () => void;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const partnerId = query.get("partnerId");
  const providerId = query.get("providerId");
  const authKey = query.get("token");
  const resmedRegistrationPath = location.pathname.includes("/resmed");

  const [step, setStep] = useState(() => STEPS.register);
  const [schedulingData, setSchedulingData] = useState<any>(undefined);
  const [bridgeProviders, setBridgeProviders] = useState<string[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>(() => {});
  const [submitting, setSubmitting] = useState(false);
  const [confirmedAccount, setConfirmedAccount] = useState(false);
  const [showResmedModal, setShowResmedModal] = useState(false);

  useEffect(() => {
    setInitialData(formData);
  }, [formData]);

  useEffect(() => {
    const { user, token } = AuthService.getAuth();
    const verifySource = async () => {
      try {
        // Verify token from url
        const response = await AuthService.verifyToken({
          token: authKey || token,
        });

        const { data } = response;

        if (data) {
          AuthService.saveAuth({
            user: { ...data },
            token: authKey as string,
          });

          setFormData((prev) => ({
            ...prev,
            state: data?.state,
            insuranceCarrier: data?.insuranceCarrier,
            refId: data?.partnerId || "",
            contactId: data?.contactId || "",
            metadata: data?.metadata || {},
          }));

          // Remove token from url
          query.delete("token");

          // add partnerId to url
          if (data.partnerId) query.set("partnerId", data.partnerId);

          history.replace({
            pathname: location.pathname,
            search: query.toString(),
          });
        } else {
          console.log(
            "No data received. Rerouting to standard registration page.",
          );
          history.replace("/");
        }
      } catch (err) {
        console.log(err);
        history.replace("/");
      }
    };

    // Save resmed metadata
    const metadata = parseQueryParams(query, ["token"]);
    if (!isEmpty(metadata)) {
      LStorage.set("referralMetadata", JSON.stringify(metadata));
    }

    if (!resmedRegistrationPath && (authKey || token)) verifySource();
  }, []);

  useEffect(() => {
    const chatbot = document.getElementById("smartbot360");
    if (!chatbot) return;

    if (step === 1) {
      chatbot.style.display = "block";
    } else {
      chatbot.style.display = "none";
    }
  }, [step]);

  const updateStep = (increment: boolean) => {
    const toSchedule = step === 5 && confirmedAccount;
    const toDetails = step === 2 && confirmedAccount && increment;

    if (step === totalSteps) return null;
    else if (toSchedule) setStep(STEPS.schedule);
    else if (toDetails) setStep(STEPS.details);
    else if (increment) setStep(step + 1);
    else setStep(step - 1);
  };

  const sendVerificationCode = (email: string) =>
    UserService.sendVerificationCode(email, "signUp", "email");

  const checkEmailAndSendCode = async (email: string) => {
    setSubmitting(true);

    try {
      const res = await UserService.checkPatientExistsByEmail(
        email.toLowerCase(),
      );
      if (res.data) {
        AuthService.cleanAuth();
        dispatch(injectAuthUser({} as any));
        history.push(`/signInPage?email=${encodeURIComponent(email)}`);
      } else {
        await UserService.sendVerificationCode(email, "signUp", "email");
        setFormData((state) => ({ ...state, email }));
        updateStep(true);
        setSubmitting(false);
      }
    } catch (error) {
      setSubmitting(false);
      toast.error(
        "We're unable to send a verification code to this email at this time!",
      );
    }
  };

  const createUser = (data: any) => {
    setSubmitting(true);

    let refId = formData?.refId || partnerId;
    if (resmedRegistrationPath) refId = "resmed";

    const contactId = query.get("contactId") as string;

    const credentials = {
      password: data?.password,
      email: formData?.email,
    };

    const carrier = formData?.insuranceCarrier;
    const insuranceCards =
      carrier && carrier !== "Carrier Not Listed"
        ? [{ insuranceCarrier: carrier }]
        : [];

    const referralMetadata = LStorage.get("referralMetadata", undefined);
    UserService.signUp({
      ...credentials,
      insuranceCards,
      platform: "Web-Patient",
      partner: refId || "",
      metadata: referralMetadata,
      state: formData?.state || "",
      lastName: formData?.lastName || "",
      firstName: formData?.firstName || "",
      contacts: contactId ? [contactId] : [],
      verificationCode: data.verificationCode,
    })
      .then(async (res) => {
        const auth = await AuthService.login(credentials);
        const data = auth?.data;

        await AuthService.saveAuth(data);
        dispatch(injectAuthUser(data?.user));

        // Remove resmed metadata
        LStorage.remove("referralMetadata");

        const patientId = getRecordId(data?.user);

        if (contactId)
          await UserService.setFavoriteContact(patientId, contactId).catch(
            () => {},
          );

        const updatedUser = {
          contacts: contactId ? [contactId] : [],
        };

        AuthService.updateUserInfo(updatedUser);
        dispatch(updateAuthUser(updatedUser));
        setConfirmedAccount(true);
        updateStep(true);
      })
      .catch((err) => toast.error(err.message || "Unable to verify the code"))
      .finally(() => setSubmitting(false));
  };

  const createAppointment = async () => {
    const { user } = AuthService.getAuth();

    const startTime = schedulingData?.start;
    const endTime = schedulingData?.end;
    const provider = schedulingData?.provider;
    let body: any = {
      endTime,
      startTime,
      description: "Consultation",
      inviteeEmail: provider?.email,
      at: dayjs(endTime).format("hh:mm a"),
      on: dayjs(startTime).format("MMM DD, YYYY"),
    };

    await UserService.createAppointment({
      ...body,
      patientId: getRecordId(user),
      providerId: getRecordId(provider),
    })
      .then((res) => {
        setSubmitting(false);
        setStep(STEPS.confirmation);
      })
      .catch((err) => {
        setSubmitting(false);
        toast.error(err.message || "Unable to create appointment");
      });
  };

  const updatePatientProfile = async (values: any) => {
    const user = AuthService.getAuth()?.user;
    const patientId = getRecordId(user);

    const carrier = values?.insuranceCarrier;
    let insuranceCards: any = get(user, "insuranceCards", []);

    if (carrier && get(insuranceCards, "0.insuranceCarrier", "") !== carrier) {
      insuranceCards = [{ insuranceCarrier: carrier }];
    }

    // Check termsConsent; process consent if false
    let termsConsent = user?.termsConsent || false;
    if (!termsConsent) {
      termsConsent = await UserService.signConsentDocuments(patientId)
        .then((res) => res?.data === true)
        .catch(() => false);
    }

    await UserService.updateProfile(
      {
        ...values,
        termsConsent,
        insuranceCards,
        timeZone: schedulingData?.timeZone || "",
        chatbotProgress: {
          sheepa: 16,
          sheepaFinished: false,
          sheepaTime: moment().utc(),
        },
      },
      patientId,
    )
      .then((res) => {
        AuthService.updateUserInfo(res.data);
        dispatch(updateAuthUser(res.data));
      })
      .catch((error) => toast.error(error.message || "Unable to save details"))
      .finally(() => setSubmitting(false));
  };

  const handleOnContinue = () => {
    if (step === 1) {
      handleDynamicFormSubmission(REGISTRATION_FORM_ID);
    } else if (step === 3) {
      handleDynamicFormSubmission(EMAIL_VERIF_FORM_ID);
    } else if (step === 4) {
      handleDynamicFormSubmission(CODE_VERIFICATION_FORM_ID);
    } else if (step === 5) {
      handleDynamicFormSubmission(PATIENT_DETAILS_FORM_ID);
    } else setStep(step + 1);
  };

  const handleSoftEligibilityCheck = async (data: any) => {
    if (step === 1) {
      const { state, insuranceCarrier } = data;
      await UserService.getSoftEligibility({
        state,
        carrier: insuranceCarrier,
      })
        .then((res) => {
          setBridgeProviders(get(res, "data.providerIds", []));
        })
        .catch(() => {});
    }
  };

  const eventStart = get(schedulingData, "start", "");
  const eventEnd = get(schedulingData, "end", "");
  const tz = get(schedulingData, "timeZone", "");

  return (
    <Box sx={{ padding: 1 }}>
      <Box paddingTop={0.5} paddingBottom={2.5}>
        <StepTracker
          totalSteps={totalSteps}
          currentStep={step}
          barHeight={6}
          gap={1}
        />
      </Box>

      <Grid container spacing={3}>
        {step === 1 && (
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              {partnerId && <ReferralWelcomeCard partnerId={partnerId} />}
              <SleepCard />
            </Stack>
          </Grid>
        )}

        {step === 1 && (
          <Grid item xs={12} md={6}>
            {resmedRegistrationPath ? (
              <FormHeaderSection
                title="Welcome to Ognomy Sleep"
                description="Please complete your details to begin your sleep journey"
              />
            ) : (
              <FormHeaderSection
                title="Let's Get Started"
                description="Already have an account?"
                link="Login"
                path="/signInPage"
              />
            )}
            <RegistrationForm
              data={pick(formData, [
                "state",
                "firstName",
                "lastName",
                "insuranceCarrier",
              ])}
              onSubmit={async (data) => {
                await handleSoftEligibilityCheck(data);
                setFormData((prev) => {
                  const state = { ...prev, ...data };
                  return state;
                });

                if (confirmedAccount) {
                  await updatePatientProfile(data);
                }

                updateStep(true);
              }}
            />
            <TermsConsent marginTop={2} />
          </Grid>
        )}

        {/* Esures scheduling component is rendered on the left at all times */}
        {step === 2 && formData?.state && (
          <Grid item xs={12}>
            <Typography
              variant="h5"
              fontSize={28}
              fontWeight={600}
              textAlign="center"
              pb={1}
            >
              Hi {formData.firstName}!
            </Typography>
            <Typography
              variant="h5"
              fontSize={28}
              fontWeight={600}
              textAlign="center"
              pb={1}
            >
              Meet Your Recommended Sleep Specialist.
            </Typography>
            <ScheduleAppointment
              hideButtons
              providerId={providerId}
              providerIds={bridgeProviders}
              state={formData?.state}
              timeZone={formData?.timeZone}
              onNoAvailability={handleNoAvailability}
              onSelect={(data) => {
                updateStep(true);
                setSchedulingData(data);
                setFormData((state) => ({ ...state, timeZone: data.timeZone }));
              }}
            />
          </Grid>
        )}

        {step > 2 && step <= 5 && (
          <>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" fontSize={28} fontWeight={600} pb={1}>
                Create an Account to Confirm Your Appointment
              </Typography>
              <EventInfo
                tz={tz}
                eventEnd={eventEnd}
                eventStart={eventStart}
                provider={schedulingData?.provider}
              />
            </Grid>
            {step <= 3 && (
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  To confirm this appointment, please enter your email address
                  and create an account. There is no cost to create your
                  account. We'll send you a verification code to get the process
                  started.
                </Typography>
                <EmailVerificationForm
                  data={{ email: formData.email }}
                  onSubmit={(data) => checkEmailAndSendCode(data.email)}
                />
                {location.pathname.includes("/resmed") && (
                  <>
                    <Typography variant="h6" fontWeight={500} pt={2}>
                      Do you have a Resmed account?
                    </Typography>
                    <Typography variant="body1" pb={1}>
                      You can proceed by logging in with your Resmed account!
                    </Typography>
                    <Button
                      fullWidth
                      sx={{
                        padding: 1,
                        borderRadius: 1,
                        backgroundColor: "background.light",
                        fontSize: "1rem",
                      }}
                      onClick={() => setShowResmedModal(true)}
                    >
                      <img
                        src={resmedLogoPath}
                        alt="Resmed Logo"
                        style={{ width: "90px", marginLeft: "10px" }}
                      />
                    </Button>
                    <Dialog
                      PaperProps={{
                        style: {
                          backgroundColor: "transparent",
                          boxShadow: "none",
                        },
                      }}
                      open={showResmedModal}
                      onClose={() => setShowResmedModal(false)}
                    >
                      <IconButton
                        sx={{
                          position: "absolute",
                          left: "90%",
                          top: "6%",
                          zIndex: 100,
                          width: 28,
                          height: 28,
                        }}
                        onClick={() => setShowResmedModal(false)}
                      >
                        <CloseIcon fontSize="medium" />
                      </IconButton>

                      <ResmedSignIn
                        source="registration"
                        registrationFormData={formData}
                        handleRegistrationLogin={(data) => {
                          setFormData((state) => ({ ...state, ...data }));
                          setShowResmedModal(false);
                          setConfirmedAccount(true);
                          setStep(STEPS.details);
                        }}
                      />
                    </Dialog>
                  </>
                )}
              </Grid>
            )}
          </>
        )}

        {step === 4 && (
          <Grid item xs={12} md={6}>
            <CodeVerification
              resendCode={() => sendVerificationCode(formData.email)}
              onSubmit={(data) => {
                setFormData((state) => ({ ...state, ...data }));
                createUser({ ...data, email: formData.email });
              }}
            />
          </Grid>
        )}

        {step === 5 && (
          <Grid item xs={12} md={6}>
            <PatientDetailsForm
              data={pick(formData, [
                "dateOfBirth",
                "phone",
                "address",
                "city",
                "zipcode",
                "gender",
              ])}
              onSubmit={async (data) => {
                setFormData((state) => ({ ...state, ...data }));
                await updatePatientProfile(data);
                await createAppointment();
              }}
            />
          </Grid>
        )}

        {step === 6 && (
          <Grid item xs={12} justifyItems="center">
            <Card sx={{ maxWidth: 700, padding: 2 }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5" pb={2} fontWeight={600}>
                  Welcome to Ognomy Sleep
                </Typography>
                <Typography pb={1}>
                  We have reserved your appointment slot for the next 72 hours.
                  To see the sleep specialist, please complete your profile now
                  or via the email reminder. If not completed within 72 hours,
                  your slot may be released and given to another patient.
                </Typography>
              </CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() =>
                    history.replace("/complete-profile?step=insurance")
                  }
                >
                  Complete My Profile
                </Button>
                <Button
                  variant="text"
                  onClick={() => history.replace("/patientDashboardPage")}
                >
                  I'll Finish Later
                </Button>
              </Box>
            </Card>
          </Grid>
        )}
      </Grid>

      {step !== totalSteps && (
        <Stack spacing={1} direction="row-reverse" sx={actionBtnStyles}>
          <LoadingButton
            type="submit"
            disabled={
              (!eventStart && step > 1) || totalSteps === step || submitting
            }
            variant="contained"
            loading={submitting}
            onClick={handleOnContinue}
          >
            Continue
          </LoadingButton>
          {step > 1 && (
            <LoadingButton
              type="submit"
              variant="contained"
              onClick={() => {
                if (step === 5) setSchedulingData(undefined);
                updateStep(false);
              }}
            >
              Back
            </LoadingButton>
          )}
        </Stack>
      )}
    </Box>
  );
};

const actionBtnStyles = {
  padding: 1,
  paddingBottom: 1.5,
  bottom: 0,
  left: 0,
  position: "fixed",
  width: "100%",
  zIndex: 100,
  backgroundColor: "background.default",
  justifyContent: "space-between",
};

export default Registration;
